import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, TrendingUp, Network, Trophy, Gem, Wallet, Users, Lock,
  ChevronRight, Hexagon, GitBranch, BadgeCheck, Sparkles,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import NetworkBg from "@/components/NetworkBg";
import LiveActivity from "@/components/LiveActivity";
import Testimonials from "@/components/Testimonials";
import LiveMarketChart from "@/components/LiveMarketChart";
import { LuxuryButton, GlassCard } from "@/components/lux";
import api, { money } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function Section({ id, eyebrow, title, children, className }) {
  return (
    <section id={id} className={`relative mx-auto max-w-7xl px-5 py-20 ${className || ""}`}>
      {eyebrow && <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[var(--gold-2)]">{eyebrow}</p>}
      {title && <h2 className="font-display text-3xl font-extrabold uppercase text-[#16a34a] sm:text-4xl">{title}</h2>}
      <div className="mt-8">{children}</div>
    </section>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/public/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const overview = [
    { icon: TrendingUp, t: "Performance Engine", d: "Configurable performance-based reward rates driven by qualifying business activity — never guaranteed returns." },
    { icon: Network, t: "Binary Architecture", d: "A dual-leg structure with transparent real, promotional, eligible and matched volume ledgers." },
    { icon: Trophy, t: "Rewards & Salary", d: "Milestone instant rewards and a daily performance salary tied to legitimately qualified targets." },
    { icon: ShieldCheck, t: "Compliance-First", d: "Investment-return components disabled by default until operators confirm applicable regulations." },
  ];

  return (
    <div className="theme-light grain min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <PublicNav />
      <LiveActivity />

      {/* HERO — sliding trading banner */}
      <HeroSlider nav={nav} />

      <LiveTicker />

      {/* LIVE MARKETS — big real-time trading chart */}
      <section id="markets" className="relative" style={{ background: "#06140d" }}>
        <div className="mx-auto max-w-7xl px-5 py-16">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[var(--gold)]">Live Markets</p>
          <h2 className="font-display text-3xl font-extrabold uppercase text-white sm:text-4xl">Real-time Forex &amp; Crypto charts</h2>
          <p className="mt-3 max-w-2xl text-sm text-[#D7E2F5]">Live BTC, ETH, Gold and major currency pairs — streamed from public market data and refreshed continuously. Switch instruments and timeframes below.</p>
          <div className="mt-8"><LiveMarketChart title="Live Market Chart" /></div>
        </div>
      </section>

      {/* OVERVIEW */}
      <Section id="about" eyebrow="Platform Overview" title="A private-banking experience for network business">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {overview.map((o) => (
            <GlassCard key={o.t} hover className="p-6">
              <div className="mb-4 grid h-12 w-12 place-items-center rounded-xl" style={{ background: "rgba(18,60,140,0.08)", border: "1px solid rgba(18,60,140,0.15)" }}>
                <o.icon size={22} className="text-[#16a34a]" />
              </div>
              <h3 className="text-lg font-bold text-[#16a34a]">{o.t}</h3>
              <p className="mt-2 text-sm text-[var(--text-dim)]">{o.d}</p>
            </GlassCard>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how" eyebrow="How It Works" title="Four steps to activate your network" className="rounded-3xl" >
        <div className="grid gap-5 md:grid-cols-4">
          {[
            { n: "01", t: "Register", d: "Join with a sponsor ID and choose your placement leg — left or right." },
            { n: "02", t: "Deposit & Activate", d: "Fund your wallet via BEP20 USDT and activate your position with qualifying business." },
            { n: "03", t: "Build Both Legs", d: "Grow left and right business volume through directs and team activity." },
            { n: "04", t: "Earn Rewards", d: "Binary matching, milestone rewards and daily performance salary accrue automatically." },
          ].map((s) => (
            <GlassCard key={s.n} hover className="p-6">
              <p className="gold-text font-display text-3xl font-extrabold">{s.n}</p>
              <h3 className="mt-3 text-lg font-bold text-[#16a34a]">{s.t}</h3>
              <p className="mt-2 text-sm text-[var(--text-dim)]">{s.d}</p>
            </GlassCard>
          ))}
        </div>
      </Section>

      {/* SECURITY */}
      <Testimonials />

      <Section id="security" eyebrow="Security & Compliance" title="Engineered for trust">
        <div className="grid gap-5 md:grid-cols-3">
          {[
            [Lock, "Bank-grade security", "Hashed passwords, session security, rate limiting, RBAC and full audit logging on sensitive actions."],
            [ShieldCheck, "Financial transparency", "Immutable ledger with reversal/adjustment records — historical entries are never overwritten."],
            [BadgeCheck, "Compliance architecture", "Configurable jurisdiction, KYC/AML review and risk disclosures. Investment returns off by default."],
          ].map(([Icon, t, d]) => (
            <GlassCard key={t} hover className="p-6">
              <Icon className="text-[#16a34a]" size={24} />
              <h3 className="mt-4 text-lg font-bold text-[#16a34a]">{t}</h3>
              <p className="mt-2 text-sm text-[var(--text-dim)]">{d}</p>
            </GlassCard>
          ))}
        </div>
      </Section>

      <TradingSection />

      {/* FINAL CTA */}
      <Section id="contact">
        <GlassCard shine className="overflow-hidden p-10 text-center sm:p-16" style={{ background: "linear-gradient(160deg, #0b3d24, #16a34a)" }}>
          <Hexagon className="mx-auto text-[var(--gold)] floaty" size={40} />
          <h2 className="mt-6 font-display text-3xl font-extrabold text-white sm:text-4xl">Ready to build with precision?</h2>
          <p className="mx-auto mt-4 max-w-xl text-[#D7E2F5]">Create your account and activate your position on a transparent, compliance-first network platform.</p>
          <div className="mt-8 flex justify-center gap-4">
            <LuxuryButton size="lg" variant="gold" onClick={() => nav("/register")} data-testid="cta-join-btn">Join Now</LuxuryButton>
            <LuxuryButton size="lg" variant="maroon" onClick={() => nav("/login")}>Login</LuxuryButton>
          </div>
          <p className="mt-8 text-sm text-[#D7E2F5]">Contact: <span className="font-semibold text-white">support@alexafintech.com</span></p>
        </GlassCard>
      </Section>

      {/* FOOTER */}
      <footer style={{ background: "#0e6b34" }} className="py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Alexa Fintech" className="h-12 w-auto object-contain" />
          </div>
          <p className="text-xs text-[#D7E2F5]">© {new Date().getFullYear()} Alexa Fintech Network. Performance-based rewards subject to program rules. No income guaranteed.</p>
        </div>
      </footer>
    </div>
  );
}

function HeroSlider({ nav }) {
  const slides = [
    { img: "/hero-trading.jpg", badge: "AI-Powered Multi-Market Trading",
      title: ["Grow Your Wealth With ", "Global AI", " Trading"],
      text: "An automated engine trades Forex, Crypto and global equities 24/7 — disciplined, data-driven execution with transparent reporting.",
      stats: [["24/7", "Bot Uptime"], ["3", "Market Classes"], ["Global", "Coverage"]] },
    { img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80", badge: "Forex \u00b7 24/5 Deep Liquidity",
      title: ["Master The ", "Forex", " Markets"],
      text: "Major and minor currency pairs traded around the clock. Algorithms read momentum, spreads and volatility to time every entry and exit.",
      stats: [["24/5", "Sessions"], ["30+", "Pairs"], ["Low", "Latency"]] },
    { img: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1920&q=80", badge: "Crypto \u00b7 Always-On Markets",
      title: ["Capture ", "Crypto", " Volatility"],
      text: "BTC, ETH and leading altcoins monitored non-stop across exchanges — the engine acts the moment an opportunity appears.",
      stats: [["24/7", "Live"], ["Multi", "Exchange"], ["Algo", "Driven"]] },
    { img: "https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1920&q=80", badge: "Equities \u00b7 Global Indices",
      title: ["Trade ", "Stocks & Indices", " Worldwide"],
      text: "Worldwide equities and indices tracked tick-by-tick with risk-managed strategies built for consistent, real growth.",
      stats: [["Global", "Equities"], ["Indices", "Tracked"], ["Risk", "Managed"]] },
  ];
  const [i, setI] = useState(0);
  useEffect(() => { const id = setInterval(() => setI((p) => (p + 1) % slides.length), 4500); return () => clearInterval(id); }, [slides.length]);
  return (
    <section id="home" className="relative overflow-hidden" style={{ background: "#06140d" }}>
      <div className="pointer-events-none absolute inset-0 z-10 opacity-40"><NetworkBg /></div>
      <div className="overflow-hidden">
        <div className="flex transition-transform duration-700 ease-out" style={{ transform: `translateX(-${i * 100}%)` }}>
          {slides.map((s, idx) => (
            <div key={idx} className="relative w-full shrink-0 overflow-hidden pt-40 pb-28">
              <img src={s.img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(6,20,13,0.94) 0%, rgba(21,128,61,0.80) 55%, rgba(21,128,61,0.30) 100%)" }} />
              <div className="relative z-20 mx-auto max-w-7xl px-5">
                <div className="max-w-3xl">
                  <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur">
                    <Sparkles size={14} className="text-[var(--gold)]" /> {s.badge}
                  </div>
                  <h1 className="font-display text-4xl font-extrabold uppercase leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                    {s.title[0]}<span className="gold-text">{s.title[1]}</span>{s.title[2]}
                  </h1>
                  <p className="mt-6 max-w-xl text-base text-[#D7E2F5]">{s.text}</p>
                  <div className="mt-9 flex flex-wrap gap-4">
                    <LuxuryButton size="lg" variant="gold" onClick={() => nav("/register")} data-testid="hero-join-btn">
                      Join Now <ChevronRight size={18} className="ml-1 inline" />
                    </LuxuryButton>
                    <LuxuryButton size="lg" variant="maroon" onClick={() => nav("/login")} data-testid="hero-login-btn">
                      Member Login
                    </LuxuryButton>
                  </div>
                  <div className="mt-12 grid max-w-lg grid-cols-3 gap-6">
                    {s.stats.map(([a, b]) => (
                      <div key={b}>
                        <p className="gold-text font-display text-2xl font-extrabold">{a}</p>
                        <p className="text-xs text-[#D7E2F5]">{b}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`}
            className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-[var(--gold)]" : "w-2 bg-white/40"}`} data-testid={`hero-dot-${idx}`} />
        ))}
      </div>
    </section>
  );
}

function LiveTicker() {
  const trades = [["EUR/USD", "Forex", "+1.8%"], ["BTC/USDT", "Crypto", "+3.2%"], ["AAPL", "Stocks", "+0.9%"], ["XAU/USD", "Forex", "+2.1%"], ["ETH/USDT", "Crypto", "+4.0%"], ["TSLA", "Stocks", "+1.4%"], ["GBP/JPY", "Forex", "+0.7%"], ["SOL/USDT", "Crypto", "+5.6%"], ["NASDAQ", "Index", "+1.1%"]];
  const row = trades.concat(trades);
  return (
    <div className="overflow-hidden border-y" style={{ borderColor: "rgba(255,255,255,0.1)", background: "#06140d" }}>
      <div className="flex gap-10 whitespace-nowrap py-3 ticker-track">
        {row.map((t, i) => (
          <span key={i} className="flex items-center gap-2 text-sm">
            <b className="text-white">{t[0]}</b>
            <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase" style={{ background: "rgba(244,196,48,0.15)", color: "#F4C430" }}>{t[1]}</span>
            <span className="font-bold text-emerald-400">{t[2]}</span>
            <span className="text-[var(--text-dim)]">·</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function TradingSection() {
  const B = "https://images.unsplash.com/";
  const markets = [
    { t: "Forex", d: "Major & minor currency pairs traded 24/5 by our automated engine.", img: `${B}photo-1551288049-bebda4e38f71?w=800&q=80` },
    { t: "Stock & Share Market", d: "Global equities and indices monitored tick-by-tick.", img: `${B}photo-1611974789855-9c2a0a7236a3?w=800&q=80` },
    { t: "Crypto Trading", d: "BTC, ETH and altcoins across leading exchanges.", img: `${B}photo-1590283603385-17ffb3a7f29f?w=800&q=80` },
  ];
  const traders = [
    { n: "Michael R.", c: "New York, USA", img: `${B}photo-1500648767791-00dcc994a43e?w=300&q=80`, line: "The AI bot's execution speed across Forex is on another level." },
    { n: "Aisyah B.", c: "Kuala Lumpur, Malaysia", img: `${B}photo-1573496359142-b8d87734a5a2?w=300&q=80`, line: "I watch the bot trade crypto 24/7 — the dashboard keeps everything clear." },
    { n: "James P.", c: "London, UK", img: `${B}photo-1560250097-0b93528c311a?w=300&q=80`, line: "Diversified across stocks and FX — genuinely a global operation." },
  ];
  return (
    <section id="trading" className="relative mx-auto max-w-7xl px-5 py-20">
      <div className="overflow-hidden rounded-3xl" style={{ background: "linear-gradient(160deg, #0b3d24, #16a34a)" }}>
        <div className="grid items-center gap-8 p-8 sm:p-12 lg:grid-cols-2">
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[var(--gold)]">AI Trading Engine</p>
            <h2 className="font-display text-3xl font-extrabold text-white sm:text-4xl">Profits powered by an automated trading Bot across global markets</h2>
            <p className="mt-4 text-[#D7E2F5]">Our proprietary trading Bot operates around the clock across <b className="text-white">Forex, Stock & Share markets and Crypto</b>, using algorithmic strategies and real-time market data. Business performance is generated by this trading activity and shared with members through the platform's reward structure.</p>
            <div className="mt-6 grid grid-cols-3 gap-4">
              {[["24/7", "Bot Uptime"], ["3", "Market Classes"], ["Global", "Coverage"]].map(([a, b]) => (
                <div key={b}><p className="gold-text font-display text-2xl font-extrabold">{a}</p><p className="text-xs text-[#D7E2F5]">{b}</p></div>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {markets.map((m) => (
              <div key={m.t} className="overflow-hidden rounded-2xl border border-white/15 bg-white/5">
                <img src={m.img} alt={m.t} className="h-28 w-full object-cover" />
                <div className="p-3"><p className="text-sm font-bold text-white">{m.t}</p><p className="mt-1 text-[11px] text-[#D7E2F5]">{m.d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10">
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-[var(--gold-2)]">Traders Worldwide</p>
        <h3 className="font-display text-2xl font-extrabold text-[#16a34a] sm:text-3xl">Members earning from Bot-driven trading, globally</h3>
        <div className="mt-6 grid gap-5 md:grid-cols-3">
          {traders.map((t) => (
            <GlassCard key={t.n} hover className="flex items-center gap-4 p-5">
              <img src={t.img} alt={t.n} className="h-16 w-16 shrink-0 rounded-full object-cover" style={{ border: "3px solid #F4C430" }} />
              <div>
                <p className="font-bold text-[#16a34a]">{t.n}</p>
                <p className="text-xs font-semibold text-[var(--maroon)]">{t.c}</p>
                <p className="mt-1 text-xs text-[var(--text-dim)]">"{t.line}"</p>
              </div>
            </GlassCard>
          ))}
        </div>
      </div>
    </section>
  );
}

function TreeNode({ label, tone }) {
  const cls = tone === "gold" ? "btn3d-gold" : tone === "maroon" ? "btn3d-maroon" : "btn3d-primary";
  return <div className={`grid h-14 w-14 place-items-center rounded-2xl text-sm font-bold ${cls}`}>{label}</div>;
}
